<template>
  <div class="order-time-usage-container">
    <!-- 顶部导航栏 -->
    <div class="page-header">
      <div class="header-left">
        <el-button icon="el-icon-arrow-left" size="small" @click="goBack">返回</el-button>
        <span class="page-title">订单时间使用统计</span>
      </div>
      <div class="header-right">
        <el-button 
          type="primary" 
          size="small" 
          icon="el-icon-refresh-right" 
          @click="fetchTimeUsage"
          :loading="loading"
        >刷新</el-button>
      </div>
    </div>

    <!-- 主要内容区 -->
    <el-card class="detail-card" v-loading="loading" shadow="hover">
      <!-- 空状态 -->
      <div v-if="!timeUsageData && !loading" class="empty-tip">
        <el-empty description="未获取到时间使用数据">
          <el-button type="primary" size="small" @click="fetchTimeUsage">重新加载</el-button>
        </el-empty>
      </div>

      <!-- 数据展示区 -->
      <div v-else-if="timeUsageData">
        <!-- 统计卡片 -->
        <div class="stats-cards">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-timer"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">合计用时</div>
              <div class="stat-value">{{ timeUsageData.totalHours || 0 }}小时 {{ timeUsageData.totalMinutes || 0 }}分钟</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-document"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">操作记录数</div>
              <div class="stat-value">{{ getTotalCount() }}条</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-map-location"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">涉及节点</div>
              <div class="stat-value">{{ getUniqueLocations() }}个</div>
            </div>
          </div>
        </div>

        <!-- 任务明细统计 -->
        <div class="section">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-s-operation"></i>
              <span>任务明细统计</span>
            </div>
            <div class="section-line"></div>
          </div>
          <el-table 
            :data="timeUsageData.details || []" 
            border 
            stripe
            class="detail-table"
            :header-cell-style="{ background: '#f5f7fa', color: '#303133', fontWeight: '600' }"
          >
            <el-table-column prop="bigType" label="操作类型" width="140">
              <template slot-scope="{row}">
                <span class="type-tag" :class="getTypeClass(row.bigType)">
                  {{ getTypeName(row.bigType) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="wareHouseName" label="仓库/地点" min-width="180">
              <template slot-scope="{row}">
                <span class="location-name">{{ row.wareHouseName || '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="count" label="操作次数" width="100" align="center">
              <template slot-scope="{row}">
                <span class="count-number">{{ row.count || 0 }}</span>
              </template>
            </el-table-column>
            <el-table-column label="开始时间" min-width="170">
              <template slot-scope="{row}">
                <div class="time-cell">
                  <i class="el-icon-time"></i>
                  <span>{{ formatDateTime(row.minOperateTime) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="结束时间" min-width="170">
              <template slot-scope="{row}">
                <div class="time-cell" v-if="row.lastOperateTime !== row.minOperateTime">
                  <i class="el-icon-time"></i>
                  <span>{{ formatDateTime(row.lastOperateTime) }}</span>
                </div>
                <span v-else class="same-time">—</span>
              </template>
            </el-table-column>
            <el-table-column label="耗时" width="150" align="center">
              <template slot-scope="{row}">
                <div class="duration-cell" :class="getDurationClass(row.minOperateTime, row.lastOperateTime)">
                  <i class="el-icon-stopwatch"></i>
                  <span>{{ calculateDuration(row.minOperateTime, row.lastOperateTime) }}</span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 运输统计卡片 -->
        <div class="section" v-if="getTransportStats().hasData">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-truck"></i>
              <span>运输统计详情</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="transport-stats">
            <div class="transport-card">
              <div class="transport-icon transport-total">
                <i class="el-icon-s-flag"></i>
              </div>
              <div class="transport-info">
                <div class="transport-label">运输合计所用时间</div>
                <div class="transport-value">{{ getTransportStats().total }}</div>
              </div>
            </div>
            <div class="transport-card">
              <div class="transport-icon transport-running">
                <i class="el-icon-video-play"></i>
              </div>
              <div class="transport-info">
                <div class="transport-label">实际运行时间</div>
                <div class="transport-value">{{ getTransportStats().running }}</div>
              </div>
            </div>
            <div class="transport-card">
              <div class="transport-icon transport-rest">
                <i class="el-icon-coffee"></i>
              </div>
              <div class="transport-info">
                <div class="transport-label">在途休息时间</div>
                <div class="transport-value">{{ getTransportStats().rest }}</div>
              </div>
            </div>
            <div class="transport-card">
              <div class="transport-icon transport-count">
                <i class="el-icon-date"></i>
              </div>
              <div class="transport-info">
                <div class="transport-label">休息次数</div>
                <div class="transport-value">{{ getTransportStats().restCount }}次</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 时间轴图表 -->
        <div class="section" v-if="getTimelineData().length">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-data-line"></i>
              <span>时间轴视图</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="timeline-container">
            <div 
              v-for="(item, index) in getTimelineData()" 
              :key="index"
              class="timeline-item"
            >
              <div class="timeline-dot" :class="getTypeClass(item.type)"></div>
              <div class="timeline-content">
                <div class="timeline-header">
                  <span class="timeline-type">{{ getTypeName(item.type) }}</span>
                  <span class="timeline-time">{{ item.duration }}</span>
                </div>
                <div class="timeline-bar">
                  <div 
                    class="timeline-bar-fill" 
                    :style="{ width: item.percentage + '%' }"
                    :class="getTypeClass(item.type)"
                  ></div>
                </div>
                <div class="timeline-location">{{ item.location || '—' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'OrderTimeUsage',
  data() {
    return {
      loading: false,
      timeUsageData: null,
      trackImageUrl: null
    }
  },
  created() {
    this.fetchTimeUsage()
  },
  methods: {
    fetchTimeUsage() {
      const orderId = this.$route.query.orderId
      if (!orderId) {
        this.$message.error('缺少订单ID参数')
        return
      }
      this.loading = true
      api.get(`/api/OrderOperation/ordertimeuse/${orderId}`)
        .then(res => {
          let result = res
          if (res && res.data && typeof res.data === 'object' && 'ret' in res.data) {
            result = res.data
          }
          if (result && result.data && result.data.ret !== undefined) {
            result = result.data
          }

          if (result && result.ret === 1000) {
            this.timeUsageData = result.data
          } else {
            this.$message.error(result?.msg || '获取数据失败')
          }
        })
        .catch(err => {
          console.error(err)
          this.$message.error('请求失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    getTotalCount() {
      const details = this.timeUsageData?.details || []
      return details.reduce((sum, item) => sum + (item.count || 0), 0)
    },
    
    getUniqueLocations() {
      const details = this.timeUsageData?.details || []
      const locations = details.map(item => item.wareHouseName).filter(Boolean)
      return new Set(locations).size
    },
    
    getTypeName(type) {
      const typeMap = {
        'Loading': '装货',
        'Load': '装货',
        'Transporting': '运输',
        'Transport': '运输',
        'Unloading': '卸货',
        'Unload': '卸货',
        'Rest': '休息',
        'TransportRest': '在途休息',
        'Running': '运行',
        'ActualRunning': '运行'
      }
      return typeMap[type] || type || '其他'
    },
    
    getTypeClass(type) {
      const classMap = {
        'Loading': 'type-loading',
        'Load': 'type-loading',
        'Transporting': 'type-transport',
        'Transport': 'type-transport',
        'Unloading': 'type-unloading',
        'Unload': 'type-unloading',
        'Rest': 'type-rest',
        'TransportRest': 'type-rest'
      }
      return classMap[type] || 'type-other'
    },
    
    getDurationClass(start, end) {
      if (!start || !end) return ''
      const minutes = this.getDurationMinutes(start, end)
      if (minutes < 10) return 'duration-short'
      if (minutes < 60) return 'duration-medium'
      return 'duration-long'
    },
    
    getDurationMinutes(start, end) {
      if (!start || !end) return 0
      const startDate = new Date(start)
      const endDate = new Date(end)
      if (isNaN(startDate) || isNaN(endDate)) return 0
      return Math.floor((endDate - startDate) / 1000 / 60)
    },
    
    getTransportStats() {
      const details = this.timeUsageData?.details || []
      
      const getDetail = (types) => {
        return details.find(d => types.includes(d.bigType))
      }
      
      const totalDetail = getDetail(['Transporting', 'Transport'])
      const runningDetail = getDetail(['Running', 'ActualRunning'])
      const restDetail = getDetail(['Rest', 'TransportRest'])
      const restCount = restDetail?.count || 0
      
      const formatDuration = (detail) => {
        if (!detail?.minOperateTime || !detail?.lastOperateTime) return '—'
        return this.calculateDuration(detail.minOperateTime, detail.lastOperateTime)
      }
      
      return {
        hasData: !!(totalDetail || runningDetail || restDetail),
        total: formatDuration(totalDetail),
        running: formatDuration(runningDetail),
        rest: formatDuration(restDetail),
        restCount: restCount
      }
    },
    
    getTimelineData() {
      const details = this.timeUsageData?.details || []
      if (!details.length) return []
      
      // 计算总时长用于百分比
      const totalMinutes = details.reduce((sum, item) => {
        return sum + this.getDurationMinutes(item.minOperateTime, item.lastOperateTime)
      }, 0)
      
      return details
        .filter(item => item.minOperateTime && item.lastOperateTime)
        .map(item => ({
          type: item.bigType,
          location: item.wareHouseName,
          duration: this.calculateDuration(item.minOperateTime, item.lastOperateTime),
          percentage: totalMinutes > 0 
            ? (this.getDurationMinutes(item.minOperateTime, item.lastOperateTime) / totalMinutes * 100).toFixed(1)
            : 0
        }))
        .sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))
    },
    
    formatDateTime(dateStr) {
      if (!dateStr) return '—'
      return dateStr.replace('T', ' ').substring(0, 19)
    },
    
    calculateDuration(start, end) {
      if (!start || !end) return '—'
      const startDate = new Date(start)
      const endDate = new Date(end)
      if (isNaN(startDate) || isNaN(endDate)) return '—'
      const diffMs = endDate - startDate
      if (diffMs < 0) return '—'
      const diffMinutes = Math.floor(diffMs / 1000 / 60)
      const hours = Math.floor(diffMinutes / 60)
      const minutes = diffMinutes % 60
      const seconds = Math.floor((diffMs % 60000) / 1000)
      
      if (hours > 0) {
        return `${hours}小时${minutes}分钟`
      } else if (minutes > 0) {
        return `${minutes}分钟${seconds}秒`
      }
      return `${seconds}秒`
    },
    
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.order-time-usage-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

/* 页面头部 */
.page-header {
  max-width: 1200px;
  margin: 0 auto 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 1px;
}

.header-right {
  display: flex;
  gap: 12px;
}

/* 卡片样式 */
.detail-card {
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 35px -10px rgba(0, 0, 0, 0.2);
}

/* 统计卡片 */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #fff 100%);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #e8eaed;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  font-size: 28px;
  color: #fff;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

/* 区块样式 */
.section {
  margin-top: 32px;
}

.section-header {
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.section-title i {
  font-size: 20px;
  color: #667eea;
}

.section-line {
  height: 3px;
  width: 50px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
}

/* 表格样式 */
.detail-table {
  border-radius: 12px;
  overflow: hidden;
}

.type-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.type-loading {
  background: #e6f7ff;
  color: #1890ff;
}

.type-transport {
  background: #fff7e6;
  color: #fa8c16;
}

.type-unloading {
  background: #f6ffed;
  color: #52c41a;
}

.type-rest {
  background: #f9f0ff;
  color: #722ed1;
}

.type-other {
  background: #f5f5f5;
  color: #8c8c8c;
}

.location-name {
  font-weight: 500;
  color: #303133;
}

.time-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #606266;
  font-size: 13px;
}

.time-cell i {
  color: #909399;
}

.same-time {
  color: #c0c4cc;
  font-size: 12px;
}

.duration-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.duration-cell i {
  font-size: 14px;
}

.duration-short {
  background: #f6ffed;
  color: #52c41a;
}

.duration-medium {
  background: #fff7e6;
  color: #fa8c16;
}

.duration-long {
  background: #fff1f0;
  color: #f5222d;
}

.count-badge {
  font-size: 14px;
  font-weight: 600;
}

/* 运输统计卡片 */
.transport-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.transport-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  border: 1px solid #e8eaed;
}

.transport-card:hover {
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.transport-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.transport-icon i {
  font-size: 24px;
}

.transport-total {
  background: #e6f7ff;
}
.transport-total i {
  color: #1890ff;
}

.transport-running {
  background: #fff7e6;
}
.transport-running i {
  color: #fa8c16;
}

.transport-rest {
  background: #f9f0ff;
}
.transport-rest i {
  color: #722ed1;
}

.transport-count {
  background: #f6ffed;
}
.transport-count i {
  color: #52c41a;
}

.transport-info {
  flex: 1;
}

.transport-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.transport-value {
  font-size: 20px;
  font-weight: 700;
  color: #303133;
}

/* 时间轴 */
.timeline-container {
  margin-top: 10px;
}

.timeline-item {
  display: flex;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.timeline-item:last-child {
  border-bottom: none;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 4px;
  flex-shrink: 0;
}

.timeline-dot.type-loading {
  background: #1890ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.2);
}

.timeline-dot.type-transport {
  background: #fa8c16;
  box-shadow: 0 0 0 3px rgba(250, 140, 22, 0.2);
}

.timeline-dot.type-unloading {
  background: #52c41a;
  box-shadow: 0 0 0 3px rgba(82, 196, 26, 0.2);
}

.timeline-dot.type-rest {
  background: #722ed1;
  box-shadow: 0 0 0 3px rgba(114, 46, 209, 0.2);
}

.timeline-content {
  flex: 1;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.timeline-type {
  font-weight: 600;
  color: #303133;
}

.timeline-time {
  font-size: 13px;
  color: #909399;
}

.timeline-bar {
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.timeline-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.timeline-bar-fill.type-loading {
  background: linear-gradient(90deg, #1890ff, #69c0ff);
}

.timeline-bar-fill.type-transport {
  background: linear-gradient(90deg, #fa8c16, #ffc53d);
}

.timeline-bar-fill.type-unloading {
  background: linear-gradient(90deg, #52c41a, #95de64);
}

.timeline-bar-fill.type-rest {
  background: linear-gradient(90deg, #722ed1, #b37feb);
}

.timeline-location {
  font-size: 12px;
  color: #909399;
}

/* 空状态 */
.empty-tip {
  text-align: center;
  padding: 60px 0;
}

/* 响应式 */
@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
  
  .transport-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .page-title {
    font-size: 16px;
  }
  
  .detail-card {
    margin: 0;
  }
  
  .stat-value {
    font-size: 18px;
  }
}
</style>